import React, { useEffect, useState, useRef, useId } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CaretDown, Check, MagnifyingGlass, Minus, Plus } from '@phosphor-icons/react';
import { Label } from '../../Label';
import { Input } from '../../Input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../Select';
const ENTER_EASE = 'ease-[cubic-bezier(0.22,1,0.36,1)]';
interface FieldWrapperProps {
  label: string;
  htmlFor?: string;
  children: React.ReactNode;
}
function FieldWrapper({
  label,
  htmlFor,
  children
}: FieldWrapperProps) {
  return <div className="flex flex-col gap-2.5" data-id="element-2">
      <Label htmlFor={htmlFor} className="text-sm font-medium text-[hsl(var(--foreground))]" data-id="element-3">
        {label}
      </Label>
      {children}
    </div>;
}
export function SelectField({
  label,
  options,
  value,
  onChange
}: {
  label: string;
  options: readonly string[];
  value: string;
  onChange: (val: string) => void;
}) {
  const id = useId();
  return <FieldWrapper label={label} htmlFor={id} data-id="element-4">
      <div className="max-w-xs" data-id="element-5">
        <Select value={value} onValueChange={onChange} data-id="element-6">
          <SelectTrigger id={id} aria-label={label} data-id="element-7">
            <SelectValue data-id="element-8" />
          </SelectTrigger>
          <SelectContent data-id="element-9">
            {options.map(opt => <SelectItem key={opt} value={opt} data-id="element-10">
                {opt}
              </SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </FieldWrapper>;
}
export function SearchableSelect({
  label,
  options,
  value,
  onChange
}: {
  label: string;
  options: readonly {
    value: string;
    label: string;
    disabled?: boolean;
  }[];
  value: string;
  onChange: (val: string) => void;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);
  const filtered = options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));
  const selectedLabel = options.find(o => o.value === value)?.label || 'Select...';
  return <FieldWrapper label={label} htmlFor={id} data-id="element-11">
      <div ref={containerRef} className="relative max-w-xs" data-id="element-12">
        <button id={id} type="button" aria-expanded={open} aria-haspopup="listbox" onClick={() => setOpen(!open)} className="flex w-full items-center justify-between rounded-[var(--radius)] border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3.5 py-2.5 text-sm text-[hsl(var(--foreground))] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-1" data-id="element-13">
          {selectedLabel}
          <CaretDown size={16} className={[`text-[hsl(var(--muted-foreground))] transition-transform duration-200 ${ENTER_EASE}`, open ? 'rotate-180' : 'rotate-0'].join(' ')} data-id="element-14" />
        </button>

        <AnimatePresence data-id="element-15">
          {open && <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.98
        }} transition={{
          duration: 0.2,
          ease: [0.22, 1, 0.36, 1]
        }} style={{
          transformOrigin: 'top center'
        }} className="absolute top-full z-10 mt-1 w-full rounded-[var(--radius)] border border-[hsl(var(--border))] bg-[hsl(var(--popover))] p-1 shadow-md" data-id="element-16">
              <div className="flex items-center gap-2 border-b border-[hsl(var(--border))] px-2 pb-2 pt-1" data-id="element-17">
                <MagnifyingGlass size={14} className="text-[hsl(var(--muted-foreground))]" data-id="element-18" />
                <input autoFocus type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." aria-label={`Search ${label}`} className="w-full bg-transparent text-sm text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none" data-id="element-19" />
              </div>
              <div className="mt-1 max-h-48 overflow-y-auto" role="listbox" aria-label={label} data-id="element-20">
                {filtered.map(opt => <button key={opt.value} type="button" role="option" aria-selected={value === opt.value} disabled={opt.disabled} onClick={() => {
              onChange(opt.value);
              setOpen(false);
              setSearch('');
            }} className={['flex w-full items-center justify-between rounded-[calc(var(--radius)-0.25rem)] px-2 py-1.5 text-left text-sm transition-colors duration-150', opt.disabled ? 'cursor-not-allowed text-[hsl(var(--muted-foreground))] opacity-50' : 'text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]'].join(' ')} data-id="element-21">
                    {opt.label}
                    {value === opt.value && <Check size={14} data-id="element-22" />}
                  </button>)}
                {filtered.length === 0 && <div className="px-2 py-3 text-center text-sm text-[hsl(var(--muted-foreground))]" data-id="element-23">
                    No results found.
                  </div>}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </FieldWrapper>;
}
export function StepperField({
  label,
  value,
  onChange,
  min = 0,
  max = 1000000,
  step = 1
}: {
  label: string;
  value: number;
  onChange: (val: number) => void;
  min?: number;
  max?: number;
  step?: number;
}) {
  return <FieldWrapper label={label} data-id="element-24">
      <div role="group" aria-label={label} className="flex max-w-[12rem] items-center rounded-[var(--radius)] border border-[hsl(var(--border))] bg-[hsl(var(--card))]" data-id="element-25">
        <button type="button" aria-label={`Decrease ${label}`} onClick={() => onChange(Math.max(min, value - step))} className={`flex h-10 w-10 items-center justify-center text-[hsl(var(--muted-foreground))] transition duration-150 ${ENTER_EASE} hover:text-[hsl(var(--foreground))] active:scale-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-inset disabled:opacity-50 disabled:active:scale-100`} disabled={value <= min} data-id="element-26">
          <Minus size={14} data-id="element-27" />
        </button>
        <div aria-live="polite" className="flex-1 overflow-hidden border-x border-[hsl(var(--border))] text-center text-sm font-medium tabular-nums text-[hsl(var(--foreground))]" data-id="element-28">
          <motion.span key={value} initial={{
          opacity: 0,
          y: 6,
          filter: 'blur(2px)'
        }} animate={{
          opacity: 1,
          y: 0,
          filter: 'blur(0px)'
        }} transition={{
          duration: 0.15,
          ease: [0.22, 1, 0.36, 1]
        }} className="inline-block" data-id="element-29">
            {value.toLocaleString()}
          </motion.span>
        </div>
        <button type="button" aria-label={`Increase ${label}`} onClick={() => onChange(Math.min(max, value + step))} className={`flex h-10 w-10 items-center justify-center text-[hsl(var(--muted-foreground))] transition duration-150 ${ENTER_EASE} hover:text-[hsl(var(--foreground))] active:scale-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-inset disabled:opacity-50 disabled:active:scale-100`} disabled={value >= max} data-id="element-30">
          <Plus size={14} data-id="element-31" />
        </button>
      </div>
    </FieldWrapper>;
}
export function NumberInput({
  label,
  value,
  onChange
}: {
  label: string;
  value: number | '';
  onChange: (val: number | '') => void;
}) {
  const id = useId();
  return <FieldWrapper label={label} htmlFor={id} data-id="element-32">
      <Input id={id} type="number" value={value} onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))} className="max-w-xs" data-id="element-33" />
    </FieldWrapper>;
}
export function DateField({
  label,
  value,
  onChange
}: {
  label: string;
  value: string;
  onChange: (val: string) => void;
}) {
  const id = useId();
  return <FieldWrapper label={label} htmlFor={id} data-id="element-34">
      <Input id={id} type="date" value={value} onChange={e => onChange(e.target.value)} className="max-w-xs" data-id="element-35" />
    </FieldWrapper>;
}
export function MultiSelectChips({
  label,
  options,
  value,
  onChange
}: {
  label: string;
  options: readonly string[];
  value: string[];
  onChange: (val: string[]) => void;
}) {
  const toggle = (opt: string) => {
    if (value.includes(opt)) onChange(value.filter(v => v !== opt));else onChange([...value, opt]);
  };
  return <FieldWrapper label={label} data-id="element-36">
      <div role="group" aria-label={label} className="flex flex-wrap gap-2" data-id="element-37">
        {options.map(opt => <button key={opt} type="button" aria-pressed={value.includes(opt)} onClick={() => toggle(opt)} className={[`rounded-full border px-3 py-1.5 text-sm transition duration-150 ${ENTER_EASE} active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2`, value.includes(opt) ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]' : 'border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]'].join(' ')} data-id="element-38">
            {opt}
          </button>)}
      </div>
    </FieldWrapper>;
}